<template>
  <div class="floating-scroll-area" :class="{ 'is-scrolling': isScrolling, 'is-dragging': dragging }">
    <div
      :id="viewportId"
      ref="viewport"
      class="floating-scroll-viewport"
      role="region"
      :aria-label="label"
      tabindex="0"
      @scroll.passive="handleScroll"
    >
      <div ref="content" class="floating-scroll-content"><slot /></div>
    </div>
    <div
      v-if="scrollRange > 0 && trackHeight > 0"
      ref="thumb"
      class="floating-scroll-thumb"
      :style="{ height: `${thumbHeight}px`, transform: `translateY(${thumbTop}px)` }"
      role="scrollbar"
      tabindex="0"
      :aria-label="`${label} scroll`"
      :aria-controls="viewportId"
      aria-orientation="vertical"
      :aria-valuemin="0"
      :aria-valuemax="Math.round(scrollRange)"
      :aria-valuenow="Math.round(scrollTop)"
      @pointerdown="startDrag"
      @pointermove="drag"
      @pointerup="endDrag"
      @pointercancel="endDrag"
      @lostpointercapture="endDrag"
      @keydown="handleKeydown"
      @click.stop
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref } from 'vue';

defineProps({ label: { type: String, required: true } });

const viewportId = `floating-scroll-${getCurrentInstance().uid}`;
const viewport = ref(null);
const content = ref(null);
const thumb = ref(null);
const viewportHeight = ref(0);
const contentHeight = ref(0);
const scrollTop = ref(0);
const dragging = ref(false);
const isScrolling = ref(false);
const inset = 4;
const scrollRange = computed(() => Math.max(0, contentHeight.value - viewportHeight.value));
const trackHeight = computed(() => Math.max(0, viewportHeight.value - inset * 2));
const thumbHeight = computed(() => Math.min(trackHeight.value, Math.max(32,
  trackHeight.value * viewportHeight.value / (contentHeight.value || 1),
)));
const travel = computed(() => trackHeight.value - thumbHeight.value);
const thumbTop = computed(() => inset + (scrollRange.value ? scrollTop.value / scrollRange.value * travel.value : 0));
let observer;
let dragState;
let scrollTimer;
let active = false;

const clamp = (value, max) => Math.max(0, Math.min(max, value));

function measure() {
  if (!active || !viewport.value) return;
  viewportHeight.value = viewport.value.clientHeight;
  contentHeight.value = viewport.value.scrollHeight;
  // Clamp the indicator during Safari's native overscroll without changing the gesture.
  scrollTop.value = clamp(viewport.value.scrollTop, scrollRange.value);
  if (!scrollRange.value || !trackHeight.value) endDrag();
}

function handleScroll() {
  measure();
  isScrolling.value = true;
  clearTimeout(scrollTimer);
  scrollTimer = setTimeout(() => { isScrolling.value = false; }, 800);
}

function setScrollTop(value) {
  if (!viewport.value) return;
  viewport.value.scrollTop = clamp(value, scrollRange.value);
  measure();
}

function startDrag(event) {
  if (event.button !== 0 || event.isPrimary === false || travel.value <= 0) return;
  event.preventDefault();
  event.stopPropagation();
  dragState = { pointerId: event.pointerId, y: event.clientY, top: scrollTop.value };
  dragging.value = true;
  thumb.value.focus({ preventScroll: true });
  thumb.value.setPointerCapture(event.pointerId);
}

function drag(event) {
  if (!dragState || event.pointerId !== dragState.pointerId || travel.value <= 0) return;
  setScrollTop(dragState.top + (event.clientY - dragState.y) * scrollRange.value / travel.value);
}

function endDrag(event) {
  if (!dragState || (event && event.pointerId !== dragState.pointerId)) return;
  const { pointerId } = dragState;
  dragState = null;
  dragging.value = false;
  if (thumb.value?.hasPointerCapture(pointerId)) thumb.value.releasePointerCapture(pointerId);
}

function handleKeydown(event) {
  const page = viewportHeight.value * 0.9;
  const positions = {
    ArrowUp: scrollTop.value - 40,
    ArrowDown: scrollTop.value + 40,
    PageUp: scrollTop.value - page,
    PageDown: scrollTop.value + page,
    Home: 0,
    End: scrollRange.value,
    ' ': scrollTop.value + (event.shiftKey ? -page : page),
  };
  if (!(event.key in positions)) return;
  event.preventDefault();
  event.stopPropagation();
  setScrollTop(positions[event.key]);
}

function observe() {
  if (active) return;
  active = true;
  observer = new ResizeObserver(measure);
  observer.observe(viewport.value);
  observer.observe(content.value);
  window.addEventListener('resize', measure);
  nextTick(measure);
}

function stopObserving() {
  active = false;
  observer?.disconnect();
  window.removeEventListener('resize', measure);
  endDrag();
  clearTimeout(scrollTimer);
  isScrolling.value = false;
}

onMounted(observe);
onActivated(observe);
onDeactivated(stopObserving);
onBeforeUnmount(stopObserving);
</script>

<style scoped>
.floating-scroll-area {
  position: relative;
  min-width: 0;
  min-height: 0;
}

.floating-scroll-viewport {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
}

.floating-scroll-viewport::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.floating-scroll-content {
  display: flow-root;
  min-width: 0;
}

.floating-scroll-thumb {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  width: 18px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: grab;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.floating-scroll-thumb::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  right: 4px;
  width: 5px;
  border-radius: 999px;
  background: var(--text-color-secondary, #606575);
  opacity: 0.28;
  transition: opacity 150ms ease;
}

.is-scrolling .floating-scroll-thumb::before,
.floating-scroll-area:focus-within .floating-scroll-thumb::before {
  opacity: 0.45;
}

.is-dragging .floating-scroll-thumb { cursor: grabbing; }

@media (hover: hover) {
  .floating-scroll-area:hover .floating-scroll-thumb::before { opacity: 0.45; }
  .floating-scroll-area:hover .floating-scroll-thumb:hover::before { opacity: 0.7; }
}

.floating-scroll-area.is-dragging .floating-scroll-thumb::before,
.floating-scroll-area .floating-scroll-thumb:focus-visible::before { opacity: 0.7; }

@media (any-pointer: coarse) {
  .floating-scroll-thumb { width: 28px; }
}

@media (prefers-reduced-motion: reduce) {
  .floating-scroll-thumb::before { transition: none; }
}

@media (forced-colors: active) {
  .floating-scroll-thumb::before { background: CanvasText; opacity: 1; }
}
</style>
